import { groq } from 'next-sanity'
import { getClient } from 'clients/sanity'
import { getQueryHelpers, QueryHelpers } from './helpers/get-query-helpers'
import { imageProjection } from './helpers/image-projection'
import type { BlogPage, FullPost, Post, PostForIndexing } from 'utils/types/sanity/post'

export const fetchPostsWithMinimalData = async (): Promise<Post['slug'][]> =>
  getClient(false).fetch<Post['slug'][]>(
    groq`*[_type == "post" && defined(slug.current)][].slug.current`
  )

export const fetchPostsForIndexing = async () =>
  getClient(false).fetch<PostForIndexing[]>(
    groq`*[_type == "post" && defined(slug.current)] { _id, "slug": slug.current, _updatedAt }`
  )

export const getPostsWithSettingsQueryHelpers = (): QueryHelpers<{}, BlogPage> =>
  getQueryHelpers(
    () => groq`
      {
        "categories": *[_type == "category"] {
          _id,
          title,
          "posts": *[_type == "post" && references(^._id)][0...10] | order(publishedAt desc) {
            _id,
            title,
            description,
            mainImage ${imageProjection},
            categories[]->{ _id, title },
            publishedAt,
            "slug": slug.current,
            "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180)
          },
        }[count(posts) > 0],
        "all": {
          "_id": "all",
          "title": "All",
          "posts": *[_type == "post"][0...10] | order(publishedAt desc) {
            _id,
            title,
            description,
            mainImage ${imageProjection},
            categories[]->{ _id, title },
            publishedAt,
            "slug": slug.current,
            "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180)
          },
        },
        "websiteSettings": *[_type == "websiteSettings"][0],
      }
    `
  )

export const getPostQueryHelpers = (): QueryHelpers<{ slug: string }, FullPost> =>
  getQueryHelpers(
    () => groq`
      *[_type == "post" && slug.current == $slug] | order(_updatedAt) [0] {
        _id,
        title,
        description,
        body,
        mainImage ${imageProjection},
        categories[]->{ _id, title },
        publishedAt,
        author->{
          name,
          jobTitle,
          image ${imageProjection},
          "slug": slug.current,
        },
        "slug": slug.current,
        "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180),
        relatedPosts[]->{ 
          _id,
          title,
          description,
          mainImage ${imageProjection},
          categories[]->{ _id, title },
          publishedAt,
          "slug": slug.current,
          "estimatedReadingTime": round(length(pt::text(body)) / 5 / 180)
        },
        "websiteSettings": *[_type == "websiteSettings"][0],
      }
    `
  )
